import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  CreditCard,
  Plus,
  Search,
  History,
  UserPlus,
  UserX,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Link2,
  Copy,
  Layers,
  Printer,
  ScanLine,
  Download,
  Trash2,
} from 'lucide-react';
import QRScannerDialog from './QRScannerDialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONT_URL = window.location.origin;

const STATUS_META = {
  available: { label: 'Available', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  assigned: { label: 'Assigned', cls: 'bg-green-500/15 text-green-400 border-green-500/40' },
  lost: { label: 'Lost', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  disabled: { label: 'Disabled', cls: 'bg-red-500/15 text-red-400 border-red-500/40' },
};

export default function StaffQRCardManager() {
  const { user } = useAuth();
  const isSupervisor = user?.role === 'supervisor';
  const [cards, setCards] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [assignDialog, setAssignDialog] = useState({ open: false, card: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, card: null, action: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, card: null, rows: [] });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStart, setBulkStart] = useState(1);
  const [bulkCount, setBulkCount] = useState(20);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [singlePdfBusy, setSinglePdfBusy] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const empUrl = isSupervisor ? `${API}/supervisor/employees` : `${API}/admin/employees`;
      const [cardsRes, empRes, supRes, evRes] = await Promise.all([
        axios.get(`${API}/admin/staff-qr-cards`),
        axios.get(empUrl),
        axios.get(`${API}/supervisor/supervisors`).catch(() => ({ data: [] })),
        axios.get(`${API}/staff/events-list`).catch(() => ({ data: { events: [] } })),
      ]);
      setCards(cardsRes.data || []);
      const emps = (empRes.data || []).filter((e) => e.status === 'active');
      const sups = (supRes.data || []).filter((s) => s.status === 'active')
        .map((s) => ({ ...s, is_supervisor: true }));
      setEmployees([...emps, ...sups].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setEvents(evRes.data?.events || []);
    } catch (_e) {
      toast.error('Failed to load cards');
    } finally {
      setLoading(false);
    }
  }, [isSupervisor]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (c.card_code || '').toLowerCase().includes(q) ||
        (c.current_employee_name || '').toLowerCase().includes(q) ||
        (c.event_label || '').toLowerCase().includes(q)
      );
    });
  }, [cards, search, statusFilter]);

  const createCard = async () => {
    const code = newCode.trim();
    if (!code) {
      toast.error('Enter a card code (e.g. QR-001)');
      return;
    }
    try {
      await axios.post(`${API}/admin/staff-qr-cards`, { card_code: code });
      toast.success(`Card ${code} created`);
      setCreateOpen(false);
      setNewCode('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    }
  };

  const runAction = async (card, path, successMsg) => {
    try {
      await axios.post(`${API}/admin/staff-qr-cards/${card.id}/${path}`);
      toast.success(successMsg);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const deleteCard = async (card) => {
    try {
      await axios.delete(`${API}/admin/staff-qr-cards/${card.id}`);
      toast.success(`${card.card_code} deleted`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openHistory = async (card) => {
    try {
      const res = await axios.get(`${API}/admin/staff-qr-cards/${card.id}/history`);
      setHistoryDialog({ open: true, card, rows: res.data || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load history');
    }
  };

  const copyUrl = (code) => {
    const url = `${FRONT_URL}/feedback/card/${encodeURIComponent(code.toLowerCase())}`;
    navigator.clipboard?.writeText(url);
    toast.success('URL copied');
  };

  // Bulk-generate sequential codes (QR-001, QR-002, ...). Skips codes that
  // already exist so filling a gap left by lost cards is idempotent.
  const bulkGenerate = async () => {
    const start = parseInt(bulkStart, 10);
    const count = parseInt(bulkCount, 10);
    if (!Number.isInteger(start) || start < 0) { toast.error('Start must be a positive number'); return; }
    if (!Number.isInteger(count) || count < 1) { toast.error('Count must be at least 1'); return; }
    if (count > 500) { toast.error('Maximum 500 cards per batch'); return; }
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/staff-qr-cards/bulk-generate`, {
        start, count, prefix: 'QR-', pad: 3,
      });
      const c = res.data?.created_count || 0;
      const s = res.data?.skipped_count || 0;
      toast.success(`Generated ${c} card${c === 1 ? '' : 's'}${s ? ` (${s} already existed)` : ''}`);
      setBulkOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk generate failed');
    } finally {
      setBulkBusy(false);
    }
  };

  // Download a printable PDF with QR codes for all currently-visible cards
  // (respects active search + status filter so admins can target one batch
  // at a time without reprinting everything).
  const downloadPdf = async () => {
    if (filtered.length === 0) { toast.error('No cards to print'); return; }
    setPdfBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/staff-qr-cards/print-pdf`,
        { card_codes: filtered.map((c) => c.card_code) },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `staff-qr-cards-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded PDF (${filtered.length} cards)`);
    } catch (e) {
      toast.error('PDF download failed');
    } finally {
      setPdfBusy(false);
    }
  };

  // Per-card PDF reprint. Reuses the same print-pdf endpoint with a single
  // code so admin can replace one lost/damaged card without rerunning a
  // batch — and without changing any other card's URL.
  const downloadSingleCardPdf = async (card) => {
    setSinglePdfBusy(card.id);
    try {
      const res = await axios.post(
        `${API}/admin/staff-qr-cards/print-pdf`,
        { card_codes: [card.card_code] },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${card.card_code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${card.card_code}.pdf`);
    } catch (e) {
      toast.error('PDF download failed');
    } finally {
      setSinglePdfBusy(null);
    }
  };

  // Scanned a physical card → find the matching DB record and open the
  // existing Assign dialog. If the card isn't registered yet we offer to
  // create it on the spot so on-site staff deployment isn't blocked.
  const handleScanned = async (code) => {
    setScannerOpen(false);
    if (!code) {
      toast.error('Could not access the camera. Use Bulk Generate instead.');
      return;
    }
    // A decoded URL that is NOT a /feedback/card/ link (e.g. the general
    // feedback poster) must never become a junk card record.
    if (/^https?:\/\//i.test(String(code))) {
      toast.error('This QR is not a staff card — it opens a website link.');
      return;
    }
    const existing = cards.find((c) => c.card_code.toLowerCase() === String(code).toLowerCase());
    if (existing) {
      if (existing.status === 'lost' || existing.status === 'disabled') {
        toast.info(`${existing.card_code} is ${existing.status} — it will be reactivated when you assign it.`);
      }
      setAssignDialog({ open: true, card: existing });
      return;
    }
    // Auto-create — keeps the on-site flow moving.
    try {
      const res = await axios.post(`${API}/admin/staff-qr-cards`, { card_code: code });
      toast.success(`Created ${res.data.card_code} — pick a staff member`);
      await fetchAll();
      setAssignDialog({ open: true, card: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unknown card');
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground p-4">Loading cards…</p>;
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code, staff or event…"
            className="pl-10 bg-background border-border rounded-sm"
            data-testid="qr-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44 bg-background border-border rounded-sm" data-testid="qr-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={() => setScannerOpen(true)}
          variant="outline"
          className="rounded-sm border-primary/40 text-primary hover:bg-primary/10"
          data-testid="qr-scan-btn"
        >
          <ScanLine className="w-4 h-4 mr-1.5" /> Scan QR Card
        </Button>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
          data-testid="qr-new-card-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New Card
        </Button>
        <Button
          onClick={() => setBulkOpen(true)}
          variant="outline"
          className="rounded-sm border-primary/40 text-primary hover:bg-primary/10"
          data-testid="qr-bulk-btn"
        >
          <Layers className="w-4 h-4 mr-1.5" /> Bulk Generate
        </Button>
        <Button
          onClick={downloadPdf}
          disabled={pdfBusy || filtered.length === 0}
          variant="outline"
          className="rounded-sm"
          data-testid="qr-pdf-btn"
        >
          <Printer className="w-4 h-4 mr-1.5" />
          {pdfBusy ? 'Building PDF…' : `Download PDF (${filtered.length})`}
        </Button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-12 text-center space-y-2">
            <CreditCard className="w-12 h-12 text-muted-foreground mx-auto opacity-50" />
            <p className="text-sm text-muted-foreground">
              {cards.length === 0 ? 'No QR cards yet. Tap "New Card" to add one.' : 'No cards match your filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((c) => (
            <QRCardRow
              key={c.id}
              card={c}
              onAssign={() => setAssignDialog({ open: true, card: c })}
              onUnassign={() => setConfirmDialog({ open: true, card: c, action: 'unassign' })}
              onLost={() => setConfirmDialog({ open: true, card: c, action: 'mark-lost' })}
              onDisable={() => setConfirmDialog({ open: true, card: c, action: 'disable' })}
              onReactivate={() => runAction(c, 'reactivate', 'Card reactivated')}
              onHistory={() => openHistory(c)}
              onCopyUrl={() => copyUrl(c.card_code)}
              onDownloadPdf={() => downloadSingleCardPdf(c)}
              onDelete={!isSupervisor && (c.feedback_count ?? 0) === 0
                ? () => setConfirmDialog({ open: true, card: c, action: 'delete' })
                : null}
              pdfBusy={singlePdfBusy === c.id}
            />
          ))}
        </div>
      )}

      {/* Bulk generate dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" /> Bulk Generate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start number</Label>
              <Input
                type="number"
                min="0"
                value={bulkStart}
                onChange={(e) => setBulkStart(e.target.value)}
                className="bg-background border-border rounded-sm"
                data-testid="qr-bulk-start"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">How many cards</Label>
              <Input
                type="number"
                min="1"
                max="500"
                value={bulkCount}
                onChange={(e) => setBulkCount(e.target.value)}
                className="bg-background border-border rounded-sm"
                data-testid="qr-bulk-count"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Will create <strong>QR-{String(parseInt(bulkStart, 10) || 0).padStart(3, '0')}</strong> through{' '}
              <strong>
                QR-{String(((parseInt(bulkStart, 10) || 0) + (parseInt(bulkCount, 10) || 0) - 1)).padStart(3, '0')}
              </strong>.
              Codes that already exist are skipped automatically.
            </p>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="outline" onClick={() => setBulkOpen(false)} className="border-border rounded-sm" disabled={bulkBusy}>
                Cancel
              </Button>
              <Button
                onClick={bulkGenerate}
                disabled={bulkBusy}
                className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
                data-testid="qr-bulk-confirm"
              >
                {bulkBusy ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase">New QR Card</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Card code</Label>
            <Input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="e.g. QR-001"
              className="bg-background border-border rounded-sm"
              data-testid="qr-new-code-input"
            />
            <p className="text-[11px] text-muted-foreground">
              This is what&apos;s printed on the back of the physical card. Each code is unique
              and case-insensitive.
            </p>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-border rounded-sm">
                Cancel
              </Button>
              <Button onClick={createCard} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm" data-testid="qr-create-confirm">
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <AssignDialog
        dialog={assignDialog}
        employees={employees}
        events={events}
        onClose={() => setAssignDialog({ open: false, card: null })}
        onAssigned={() => { setAssignDialog({ open: false, card: null }); fetchAll(); }}
      />

      {/* Destructive confirmation dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(o) => { if (!o) setConfirmDialog({ open: false, card: null, action: null }); }}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow tracking-wider uppercase">
              {confirmDialog.action === 'unassign' && 'Remove assignment?'}
              {confirmDialog.action === 'mark-lost' && 'Mark card as lost?'}
              {confirmDialog.action === 'disable' && 'Disable card?'}
              {confirmDialog.action === 'delete' && 'Delete card permanently?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'unassign' && 'The card returns to Available. Existing feedback stays linked to the previous staff member.'}
              {confirmDialog.action === 'mark-lost' && 'The card is marked Lost and stops accepting feedback. Historical feedback is preserved.'}
              {confirmDialog.action === 'disable' && 'The card is disabled and stops accepting feedback. Reactivate it later if needed.'}
              {confirmDialog.action === 'delete' && 'The card is permanently deleted. Only possible because it has no feedback linked.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const c = confirmDialog.card;
                const a = confirmDialog.action;
                setConfirmDialog({ open: false, card: null, action: null });
                if (a === 'unassign') runAction(c, 'unassign', 'Assignment removed');
                if (a === 'mark-lost') runAction(c, 'mark-lost', 'Card marked as lost');
                if (a === 'disable') runAction(c, 'disable', 'Card disabled');
                if (a === 'delete') deleteCard(c);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-sm"
              data-testid="qr-confirm-action"
            >
              Yes, continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Camera-based QR scanner — scanning a physical card opens the
          Assign dialog pre-targeted at that card (creating it if needed). */}
      <QRScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScanned}
      />

      {/* History dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(o) => { if (!o) setHistoryDialog({ open: false, card: null, rows: [] }); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              History — {historyDialog.card?.card_code}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {historyDialog.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No history yet.</p>
            ) : historyDialog.rows.map((h) => (
              <div key={h.id} className="border-l-2 border-border pl-3 py-1.5">
                <p className="text-sm">
                  <span className="text-foreground capitalize">{(h.action || '').replace('_', ' ')}</span>
                  {h.employee_name && <> · <span className="text-foreground">{h.employee_name}</span></>}
                  {h.assignment_type && <> · <span className="text-muted-foreground capitalize">{h.assignment_type.replace('_', ' ')}</span></>}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {h.at ? format(parseISO(h.at), 'dd MMM yyyy · HH:mm') : ''}
                  {h.actor_name && <> · by {h.actor_name}</>}
                  {h.event_label && <> · {h.event_label}</>}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QRCardRow({ card, onAssign, onUnassign, onLost, onDisable, onReactivate, onHistory, onCopyUrl, onDownloadPdf, onDelete, pdfBusy }) {
  const meta = STATUS_META[card.status] || STATUS_META.available;
  return (
    <Card className="bg-card border-border rounded-sm" data-testid={`qr-card-${card.card_code}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-base font-semibold flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-primary" />
            {card.card_code}
          </p>
          <Badge className={`text-[10px] uppercase tracking-wider border ${meta.cls}`}>{meta.label}</Badge>
        </div>

        {card.current_employee_id ? (
          <div className="text-xs space-y-0.5">
            <p>
              <span className="text-muted-foreground">Assigned to:</span>{' '}
              <span className="text-foreground font-medium">{card.current_employee_name}</span>
            </p>
            <p>
              <span className="text-muted-foreground">Type:</span>{' '}
              <span className="capitalize">{(card.assignment_type || '').replace('_', ' ')}</span>
              {card.event_label && <> · {card.event_label}</>}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">Unassigned</p>
        )}

        <p className="text-[11px] text-muted-foreground flex items-center gap-1 break-all">
          <Link2 className="w-3 h-3" />
          /feedback/card/{(card.card_code || '').toLowerCase()}
          <button
            type="button"
            onClick={onCopyUrl}
            className="ml-1 text-primary hover:text-yellow-300"
            title="Copy URL"
            data-testid={`qr-copy-${card.card_code}`}
          >
            <Copy className="w-3 h-3" />
          </button>
        </p>

        <p className="text-[11px] text-muted-foreground">
          Total feedback: <span className="text-foreground">{card.feedback_count ?? 0}</span>
        </p>

        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" onClick={onAssign} className="rounded-sm text-xs border-primary/30 text-primary hover:bg-primary/10" data-testid={`qr-assign-${card.card_code}`}>
            <UserPlus className="w-3 h-3 mr-1" />
            {card.current_employee_id ? 'Reassign' : 'Assign'}
          </Button>
          {card.current_employee_id && (
            <Button size="sm" variant="outline" onClick={onUnassign} className="rounded-sm text-xs">
              <UserX className="w-3 h-3 mr-1" />Remove
            </Button>
          )}
          {card.status !== 'lost' && card.status !== 'disabled' && (
            <Button size="sm" variant="outline" onClick={onLost} className="rounded-sm text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10">
              <AlertTriangle className="w-3 h-3 mr-1" />Lost
            </Button>
          )}
          {card.status !== 'disabled' && card.status !== 'lost' && (
            <Button size="sm" variant="outline" onClick={onDisable} className="rounded-sm text-xs border-red-500/30 text-red-400 hover:bg-red-500/10">
              <Ban className="w-3 h-3 mr-1" />Disable
            </Button>
          )}
          {(card.status === 'lost' || card.status === 'disabled') && (
            <Button size="sm" variant="outline" onClick={onReactivate} className="rounded-sm text-xs border-green-500/30 text-green-400 hover:bg-green-500/10" data-testid={`qr-reactivate-${card.card_code}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" />Reactivate
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onHistory} className="rounded-sm text-xs" data-testid={`qr-history-${card.card_code}`}>
            <History className="w-3 h-3 mr-1" />History
          </Button>
          {onDelete && (
            <Button size="sm" variant="ghost" onClick={onDelete} className="rounded-sm text-xs text-red-400 hover:bg-red-500/10" data-testid={`qr-delete-${card.card_code}`}>
              <Trash2 className="w-3 h-3 mr-1" />Delete
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={onDownloadPdf}
            disabled={pdfBusy}
            className="rounded-sm text-xs"
            title={`Re-download printable PDF for ${card.card_code}`}
            data-testid={`qr-pdf-${card.card_code}`}
          >
            <Download className="w-3 h-3 mr-1" />{pdfBusy ? '…' : 'PDF'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AssignDialog({ dialog, employees, events, onClose, onAssigned }) {
  const [empId, setEmpId] = useState('');
  const [type, setType] = useState('permanent');
  const [eventId, setEventId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dialog.open) {
      setEmpId(dialog.card?.current_employee_id || '');
      setType(dialog.card?.assignment_type || 'permanent');
      setEventId(dialog.card?.event_id || '');
    }
  }, [dialog]);

  const save = async () => {
    if (!empId) {
      toast.error('Pick a staff member');
      return;
    }
    if (type === 'event_only' && !eventId) {
      toast.error('Pick an event');
      return;
    }
    setSaving(true);
    try {
      const ev = events.find((e) => e.group_key === eventId);
      await axios.post(`${API}/admin/staff-qr-cards/${dialog.card.id}/assign`, {
        employee_id: empId,
        assignment_type: type,
        event_id: type === 'event_only' ? eventId : null,
        event_label: type === 'event_only' ? (ev?.location || '') : null,
      });
      toast.success('Card assigned');
      onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assign failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-wider uppercase">
            Assign card {dialog.card?.card_code}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Staff member</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger className="bg-background border-border rounded-sm" data-testid="qr-assign-emp">
                <SelectValue placeholder="Pick a staff member…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}{e.is_supervisor ? ' — Supervisor' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Assignment type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-background border-border rounded-sm" data-testid="qr-assign-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">Permanent</SelectItem>
                <SelectItem value="event_only">Event Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === 'event_only' && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Event</Label>
              <Select value={eventId} onValueChange={setEventId}>
                <SelectTrigger className="bg-background border-border rounded-sm" data-testid="qr-assign-event">
                  <SelectValue placeholder="Pick an event…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {events.map((ev) => (
                    <SelectItem key={ev.group_key} value={ev.group_key}>
                      {ev.location} · {ev.position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="border-border rounded-sm" disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
              data-testid="qr-assign-confirm"
            >
              {saving ? 'Saving…' : 'Confirm'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
